import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Box, FabButton, Flex, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { useTranslation } from '@shared/core';
import { Share } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { withWindow } from '@shared/utils/withWindow';
import { useHotelBookingRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { AccommodationCombinedType } from '../../Accommodation.types';
import { useCustomPageTelemetry } from '@apps/guest/packages/layout-engine/widgets/Custom/Custom.telemetry';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';

type TelemetryHotelInfo = {
  hotelName: string;
  isHotelPreferred: boolean;
  hotelPrice: number | undefined;
  hotelStrikeThroughPrice: number | undefined;
};

export const AccommodationTileImage: React.FC<{
  id: Maybe<string>;
  showDefaultData: Maybe<boolean>;
  photoUrl: Maybe<string>;
  eventHandle: Maybe<string>;
  telemetryHotelInfo: TelemetryHotelInfo;
  type: AccommodationCombinedType;
  interestedGuestCount: Maybe<number>;
}> = ({ showDefaultData, photoUrl, eventHandle, telemetryHotelInfo, id, type, interestedGuestCount }) => {
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const hasShare = Boolean(navigator.share);
  const { roomblock, buildPath } = useHotelBookingRoutePaths();
  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const showGuestCount = interestedGuestCount && interestedGuestCount > 1;

  const telemetry = useCustomPageTelemetry();

  const handleShareBookinglink = useEventCallback(() => {
    telemetry.shareRoomClicked('accommodations', telemetryHotelInfo);
    withWindow(global => navigator.share({ url: `${global.location.origin}${buildPath(eventHandle || '', roomblock, id || '')}` }));
  });

  if (type !== AccommodationCombinedType.Roomblock && !photoUrl) return null;

  return (
    <Box
      width={'100%'}
      height={250}
      minWidth={56}
      borderRadius={'12px'}
      objectFit={'cover'}
      backgroundSize={'cover'}
      backgroundPosition={`center ${showDefaultData ? 'bottom' : 'center'}`}
      backgroundRepeat={'no-repeat'}
      backgroundImage={`url(${photoUrl || DEFAULT_HOTEL_IMAGE})`}
      display="flex"
      position={'relative'}
    >
      {showGuestCount && type === AccommodationCombinedType.Roomblock && (
        <Flex
          position={'absolute'}
          backgroundColor={'rgba(255, 255, 255, 0.80)'}
          backdropFilter={'blur(3.75px)'}
          borderRadius={pxToRem(25)}
          top={5}
          left={3}
          width={pxToRem(190)}
          height={pxToRem(38)}
          paddingY={1}
          paddingLeft={1}
        >
          <Flex
            justifyContent="center"
            alignItems={'center'}
            borderRadius={'50%'}
            width={pxToRem(30)}
            height={pxToRem(30)}
            backgroundColor={'white'}
          >
            <TextV2
              fontFamily={'Inter UI'}
              color={'mono14'}
              fontSize={pxToRem(15)}
              fontWeight={600}
              padding={pxToRem(5)}
            >
              {interestedGuestCount}
            </TextV2>
          </Flex>
          <TextV2
            fontFamily={'Inter UI'}
            color={'black'}
            fontSize={pxToRem(13)}
            letterSpacing={pxToRem(-0.032)}
            fontWeight={600}
            paddingLeft={3}
            alignSelf={'center'}
          >
            {accTrans.guestCount()}
          </TextV2>
        </Flex>
      )}
      {isMobile && hasShare && !showGuestCount && (
        <FabButton
          position={'absolute'}
          variant="ghostShadow"
          onClick={handleShareBookinglink}
          top={5}
          right={5}
          width={pxToRem(32)}
          height={pxToRem(32)}
          minWidth={pxToRem(7)}
          minHeight={pxToRem(7)}
          padding={0}
        >
          <Share size="sm" />
        </FabButton>
      )}
      {isMobile && hasShare && showGuestCount && (
        <FabButton
          position={'absolute'}
          variant="ghostShadow"
          backgroundColor={'rgba(255, 255, 255, 0.80)'}
          backdropFilter={'blur(3.75px)'}
          color={'black'}
          onClick={handleShareBookinglink}
          top={5}
          right={5}
          width={pxToRem(40)}
          height={pxToRem(40)}
          minWidth={pxToRem(7)}
          minHeight={pxToRem(7)}
          padding={0}
        >
          <Share size="sm" />
        </FabButton>
      )}
    </Box>
  );
};
